import './arvore.css'

import type { PermissionName } from '@jgp-er-dev/permissions'
import classNames from 'classnames'
import React, { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { useUser } from '../../contexts/UserContext'
import type { DisplayType } from '../../pages/portfolio/utils/constants'
import MenuIcon from '../icons/Menu'
import Input from '../Input'
import Scrollbar from '../Scrollbar'
import { NodeLink } from './NodeLink'

interface Props {
  checkPermissions: (permissions: PermissionName[]) => boolean
  className?: string
  display: DisplayType
  tree: SideBarTreeSection
  open: boolean
  onClose: () => void
}

export interface SideBarTreeSection {
  title: string
  children: (SideBarTreeSection | SideBarTreeLink)[]
  permissions?: PermissionName[]
  /**
   * DENTRO desta seção, os submenus abrem em ÁRVORE, no lugar, em vez de
   * continuar trocando a lista a cada nível.
   *
   * A seção marcada continua sendo uma ENTRADA: clicar nela entra na tela dela,
   * como qualquer outra. A diferença está lá dentro — os níveis abaixo abrem e
   * fecham no lugar, então dá para ver dois galhos ao mesmo tempo e pular entre
   * páginas sem refazer o caminho.
   *
   * Fica como opção (e não como o novo padrão) porque o menu é compartilhado:
   * os outros ramos de topo são de outras áreas da empresa e seguem no
   * comportamento antigo. Ligada hoje só na Credit Regia (ver utils/constants).
   */
  expandir?: boolean
  /** Ícone da ÁREA — só aparece no nível de cima da árvore (`expandir`). */
  icone?: IconeMenu
}

export interface SideBarTreeLink {
  title: string
  path: string
  permissions?: PermissionName[]
  /** Ícone da ÁREA — só aparece no nível de cima da árvore (`expandir`). */
  icone?: IconeMenu
}

export type IconeMenu =
  | 'cadastro'
  | 'esg'
  | 'credito-liquido'
  | 'credito-iliquido'

type SideBarTreeNode = SideBarTreeSection | SideBarTreeLink

interface SideBarTreeSectionWithParent extends SideBarTreeSection {
  children: (SideBarTreeSectionWithParent | SideBarTreeLink)[]
  parent: SideBarTreeSectionWithParent | null
}

interface SearchHit {
  title: string
  path: string
  sectionPath: string
}

const isLink = (node: SideBarTreeNode): node is SideBarTreeLink => {
  return node.hasOwnProperty('path')
}

const processTree = (
  tree: SideBarTreeNode,
  parent: SideBarTreeSectionWithParent | null = null
) => {
  if (isLink(tree)) {
    return {
      title: tree.title,
      path: tree.path,
      permissions: tree.permissions,
      icone: tree.icone,
      parent,
    } as SideBarTreeLink
  }

  const node: SideBarTreeSectionWithParent = {
    title: tree.title,
    permissions: tree.permissions,
    expandir: tree.expandir,
    icone: tree.icone,
    children: [],
    parent,
  }

  node.children = tree.children.map((child) => processTree(child, node))

  return node
}

/** Chave de abertura de uma seção: o caminho de títulos até ela. Dois "Reports"
 *  em ramos diferentes são seções diferentes e abrem sozinhos. */
const chaveDe = (trilha: string[]) => trilha.join(' / ')

/** Uma seção só aparece se levar a ALGUM link que o usuário pode ver. No modo
 *  de entrar e voltar isso não importava: a seção vazia só se revelava depois
 *  do clique que trocava de tela. Numa árvore que abre no lugar, ela viraria um
 *  item que expande para o nada. */
const temLinkVisivel = (
  no: SideBarTreeNode,
  pode: (permissions: PermissionName[]) => boolean
): boolean => {
  if (!pode(no.permissions ?? [])) return false
  if (isLink(no)) return true
  return no.children.some((filho) => temLinkVisivel(filho, pode))
}

/** Seções na trilha até a rota aberta — para a árvore já nascer aberta no lugar
 *  onde o usuário está, em vez de exigir os mesmos cliques toda vez. */
const trilhaAteRota = (
  no: SideBarTreeNode,
  rota: string,
  trilha: string[] = []
): string[] | null => {
  if (isLink(no)) {
    if (!no.path || !no.path.startsWith('/')) return null
    return rota === no.path || rota.startsWith(`${no.path}/`) ? trilha : null
  }
  for (const filho of no.children) {
    const achou = trilhaAteRota(filho, rota, trilha.concat(no.title))
    if (achou) return achou
  }
  return null
}

interface NoProps {
  no: SideBarTreeNode
  trilha: string[]
  nivel: number
  abertos: Set<string>
  alternar: (chave: string) => void
  pode: (permissions: PermissionName[]) => boolean
  rota: string
}

const rotaAtiva = (path: string, rota: string) =>
  !!path &&
  path.startsWith('/') &&
  (rota === path || rota.startsWith(`${path}/`))

/** Desenhos dos ícones das áreas (Lucide, ISC), no traço da cor do texto:
 *  acompanham o verde da área aberta. */
const DESENHOS: Record<IconeMenu, React.ReactNode> = {
  cadastro: (
    <>
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M3 5v14a9 3 0 0 0 18 0V5" />
      <path d="M3 12a9 3 0 0 0 18 0" />
    </>
  ),
  esg: (
    <>
      <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z" />
      <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" />
    </>
  ),
  'credito-liquido': (
    <>
      <path d="M3 3v18h18" />
      <path d="m19 9-5 5-4-4-3 3" />
    </>
  ),
  'credito-iliquido': (
    <>
      <path d="m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z" />
      <path d="m22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65" />
      <path d="m22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65" />
    </>
  ),
}

/** Área sem ícone ganha o quadro vazio: os títulos seguem alinhados. */
const Icone: React.FC<{ nome?: IconeMenu }> = ({ nome }) => (
  <svg
    aria-hidden
    className="sb-icone"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {nome ? DESENHOS[nome] : null}
  </svg>
)

const Seta: React.FC = () => (
  <svg
    aria-hidden
    className="sb-seta"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 3.5 10.5 8 6 12.5" />
  </svg>
)

/** Um nó do ramo em modo ÁRVORE. A seção abre e fecha no lugar, então dá para
 *  ver duas áreas ao mesmo tempo e pular de uma página para outra sem refazer o
 *  caminho. Só é usado sob uma seção marcada com `expandir`.
 *
 *  O papel sai do lugar na árvore (ver arvore.css): nível 0 é ÁREA; seção mais
 *  abaixo é CABEÇALHO DE GRUPO; link mais abaixo é PÁGINA. Os rótulos levam
 *  `break-words`, senão um título longo ("Portfolio FIDC - Desenvolve SP")
 *  estica o container `items-start` e a barra rola na horizontal. */
const NoDaArvore: React.FC<NoProps> = (props) => {
  const { no, trilha, nivel, abertos, alternar, pode, rota } = props
  if (!temLinkVisivel(no, pode)) return null

  if (isLink(no)) {
    const ativo = rotaAtiva(no.path, rota)
    const area = nivel === 0
    return (
      <NodeLink
        className={classNames(area ? 'sb-l1' : 'sb-l3', ativo && 'sb-ativa')}
        to={no.path}
        ariaCurrent={ativo ? 'page' : undefined}
      >
        {area && <Icone nome={no.icone} />}
        <span
          className={classNames(
            'break-words',
            area ? 'min-w-0 flex-1' : 'block'
          )}
        >
          {no.title}
        </span>
      </NodeLink>
    )
  }

  const chave = chaveDe(trilha.concat(no.title))
  const aberta = abertos.has(chave)
  const area = nivel === 0

  return (
    <>
      <button
        type="button"
        aria-expanded={aberta}
        className={classNames(area ? 'sb-l1' : 'sb-l2', aberta && 'sb-aberta')}
        // cabeçalho de grupo mais fundo que Gestão/Análise: um degrau a mais
        style={area ? undefined : { paddingLeft: 32 + (nivel - 1) * 16 }}
        onClick={() => alternar(chave)}
      >
        {area && <Icone nome={no.icone} />}
        <span className="min-w-0 flex-1 break-words">{no.title}</span>
        <Seta />
      </button>
      {aberta && (
        <div className={classNames(area && 'sb-ramo')}>
          <Filhos {...props} trilha={trilha.concat(no.title)} no={no} />
        </div>
      )}
    </>
  )
}

/** Filhos de uma seção aberta. Páginas seguidas vão juntas sob a linha-guia
 *  (`.sb-grupo`), com o nome da seção para leitores de tela. Filtra antes de
 *  agrupar: um grupo só de páginas sem permissão seria uma linha vazia. */
const Filhos: React.FC<NoProps & { no: SideBarTreeSection }> = (props) => {
  const { no, nivel, pode } = props
  const blocos: (SideBarTreeNode | SideBarTreeLink[])[] = []
  for (const filho of no.children) {
    if (!temLinkVisivel(filho, pode)) continue
    const ultimo = blocos[blocos.length - 1]
    if (!isLink(filho)) blocos.push(filho)
    else if (Array.isArray(ultimo)) ultimo.push(filho)
    else blocos.push([filho])
  }

  return (
    <>
      {blocos.map((bloco, i) =>
        Array.isArray(bloco) ? (
          <div
            key={`grupo-${i}`}
            role="group"
            aria-label={no.title}
            className="sb-grupo"
            // a linha fica sob o cabeçalho do grupo: degrau a cada nível abaixo
            style={{ marginLeft: 36 + Math.max(0, nivel - 1) * 16 }}
          >
            {bloco.map((link) => (
              <NoDaArvore
                {...props}
                key={link.title}
                no={link}
                nivel={nivel + 1}
              />
            ))}
          </div>
        ) : (
          <NoDaArvore
            {...props}
            key={bloco.title}
            no={bloco}
            nivel={nivel + 1}
          />
        )
      )}
    </>
  )
}

const SideBar: React.FC<Props> = (props) => {
  const tree = useMemo(
    () => processTree(props.tree) as SideBarTreeSectionWithParent,
    [props.tree]
  )
  const [curNode, setCurNode] = useState<SideBarTreeSectionWithParent>(tree)
  const [menuAppearSide, setMenuAppearSide] = useState<'left' | 'right' | null>(
    null
  )
  const [closing, setClosing] = useState<boolean>(false)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [curDepth, setCurDepth] = useState<number>(0)
  // Seções abertas no ramo em modo árvore, por caminho. Mora aqui para
  // sobreviver a fechar e reabrir a barra: a SideBar fica montada o tempo todo,
  // então o que o usuário deixou aberto continua aberto na próxima vez.
  const [abertos, setAbertos] = useState<Set<string>>(new Set())

  const { theme } = useUser()
  const { pathname } = useLocation()

  // Títulos da raiz até o nó aberto, SEM a raiz. As chaves de `abertos` são
  // absolutas: a árvore começa abaixo da seção marcada, mas a chave continua
  // sendo o caminho inteiro, que é o mesmo que `trilhaAteRota` produz.
  const trilhaAtual = useMemo(() => {
    const titulos: string[] = []
    let no: SideBarTreeSectionWithParent | null = curNode
    while (no && no.parent) {
      titulos.unshift(no.title)
      no = no.parent
    }
    return titulos
  }, [curNode])

  const alternar = (chave: string) =>
    setAbertos((atual) => {
      const proximo = new Set(atual)
      if (proximo.has(chave)) proximo.delete(chave)
      else proximo.add(chave)
      return proximo
    })

  // Põe o menu no lugar onde o usuário JÁ ESTÁ: entra na seção marcada que
  // contém a rota aberta e deixa a trilha até ela expandida. Sem isto, estando
  // em Research era preciso abrir o menu, entrar em Credit Regia e reabrir
  // Análise só para chegar na tela vizinha — o caminho que a árvore veio
  // encurtar. Só vale para a seção marcada: as outras áreas abrem na raiz,
  // como sempre.
  useEffect(() => {
    const trilha = trilhaAteRota(props.tree, pathname)
    if (!trilha?.length) return

    setAbertos((atual) => {
      const proximo = new Set(atual)
      // começa em 1: a posição 0 é o título da RAIZ, que não é uma seção
      for (let i = 1; i < trilha.length; i++)
        proximo.add(chaveDe(trilha.slice(1, i + 1)))
      return proximo
    })

    // desce a trilha na árvore processada até achar a seção marcada
    let no = tree
    for (let i = 1; i < trilha.length; i++) {
      const filho = no.children.find(
        (c) => !isLink(c) && c.title === trilha[i]
      ) as SideBarTreeSectionWithParent | undefined
      if (!filho) return
      no = filho
      if (no.expandir) {
        setCurNode(no)
        setCurDepth(i)
        setMenuAppearSide(null)
        return
      }
    }
  }, [props.tree, tree, pathname])

  const buttonClass = classNames(
    theme?.compass.sideBar.listItemBackgroundColor ??
      'hover:bg-success-dark focus:bg-success-dark',
    'focus:outline-none'
  )

  const handleClose = () => {
    setMenuAppearSide(null)
    setClosing(true)
    setTimeout(() => {
      setClosing(false)
      props.onClose?.()
    }, 200)
  }

  const handleBack = () => {
    setCurDepth((depth) => depth - 1)
    setMenuAppearSide('left')
    setCurNode(curNode.parent!)
  }

  const searchResults = useMemo(() => {
    const results: SearchHit[] = []

    const dfs = (node: SideBarTreeNode, path: string[] = []) => {
      if (isLink(node)) {
        if (
          node.title
            .toLocaleLowerCase()
            .includes(searchQuery.toLocaleLowerCase())
        ) {
          results.push({
            title: node.title,
            path: node.path,
            sectionPath: path
              .slice(1)
              .map((title) => `${title} >`)
              .join(' '),
          })
        }
        return
      }

      node.children.forEach((child) => dfs(child, path.concat(node.title)))
    }

    dfs(tree)

    return results
  }, [tree, searchQuery])

  return (
    <div
      className={classNames('fixed inset-0 z-20', {
        hidden: !props.open,
        'bg-black bg-opacity-25': props.open && !closing,
      })}
      onClick={handleClose}
    >
      <div
        className={classNames(
          'absolute left-0 flex h-full min-h-screen w-72 animate-translate-from-left flex-col overflow-hidden pr-2 pb-4 text-lg text-white',
          theme?.compass.sideBar.backgroundColor,
          {
            '-translate-x-full transform transition-transform duration-200':
              closing,
          }
        )}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex h-12 flex-shrink-0 items-center px-4">
          <button
            className="bg-white bg-opacity-0 hover:bg-opacity-10 focus:outline-none focus-visible:bg-opacity-10"
            onClick={handleClose}
          >
            <MenuIcon className="h-8 text-white" />
          </button>
          {theme && <theme.compass.sideBar.Logo />}
        </div>
        <div className="my-2 w-full px-4">
          <Input
            className={classNames(
              'w-full',
              theme?.compass.sideBar.searchInputBackgroundColor ??
                'bg-info-light'
            )}
            placeholder="Buscar"
            value={searchQuery}
            onChange={(newQuery) => setSearchQuery(newQuery)}
          />
        </div>
        <Scrollbar className="mt-4 flex-grow">
          <div
            key={curDepth}
            className={classNames('flex w-full flex-col items-start', {
              'animate-translate-from-left': menuAppearSide === 'left',
              'animate-translate-from-right': menuAppearSide === 'right',
            })}
          >
            {searchQuery ? (
              searchResults.length === 0 ? (
                <div className="px-4 text-base">
                  Nenhum resultado foi encontrado
                </div>
              ) : (
                searchResults.map((searchHit, index) => (
                  <NodeLink
                    key={index}
                    className={classNames('w-full px-4 py-2', buttonClass)}
                    to={searchHit.path}
                  >
                    {searchHit.sectionPath && (
                      <div className="text-left text-sm">
                        {searchHit.sectionPath}
                      </div>
                    )}
                    <div className="text-left">{searchHit.title}</div>
                  </NodeLink>
                ))
              )
            ) : (
              <>
                {curNode.expandir ? (
                  // Cabeçalho da árvore: o "voltar" diz PARA ONDE volta, no
                  // rótulo pequeno dos cabeçalhos de grupo, e a área em
                  // destaque logo abaixo. É a única volta da seção — o
                  // "< Voltar" do rodapé repetia a mesma ação.
                  <div className="sb-topo">
                    {curNode.parent !== null && (
                      <button
                        type="button"
                        className="sb-voltar"
                        aria-label={`Voltar para ${
                          curNode.parent.title || 'o menu'
                        }`}
                        onClick={handleBack}
                      >
                        <Seta />
                        <span>{curNode.parent.title || 'Voltar'}</span>
                      </button>
                    )}
                    <div className="sb-titulo">{curNode.title}</div>
                  </div>
                ) : (
                  curNode.title && (
                    <div
                      className={classNames(
                        'mb-4 flex h-14 items-center pr-4 font-bold',
                        {
                          'pl-4': !curNode.parent,
                        }
                      )}
                    >
                      {curNode.parent !== null && (
                        <button
                          className={classNames(
                            'mr-2 px-4 py-2 text-left',
                            buttonClass
                          )}
                          onClick={handleBack}
                        >
                          {'<'}
                        </button>
                      )}
                      {curNode.title}
                    </div>
                  )
                )}
                {/* Dentro de uma seção marcada com `expandir` (hoje a Credit
                    Regia), os níveis abaixo viram ÁRVORE: abrem no lugar em vez
                    de trocar a lista de novo. Entrar na seção segue igual. */}
                {curNode.expandir
                  ? curNode.children.map((child) => (
                      <NoDaArvore
                        key={child.title}
                        no={child}
                        trilha={trilhaAtual}
                        nivel={0}
                        abertos={abertos}
                        alternar={alternar}
                        pode={props.checkPermissions}
                        rota={pathname}
                      />
                    ))
                  : curNode.children.map((child) =>
                      !props.checkPermissions(
                        child.permissions ?? []
                      ) ? null : isLink(child) ? (
                        <NodeLink
                          key={child.title}
                          className={classNames(
                            'w-full px-4 py-2 text-left',
                            buttonClass
                          )}
                          to={child.path}
                        >
                          {child.title}
                        </NodeLink>
                      ) : (
                        <button
                          key={child.title}
                          className={classNames(
                            'flex w-full items-center justify-between px-4 py-2',
                            buttonClass
                          )}
                          onClick={() => {
                            setCurDepth((depth) => depth + 1)
                            setMenuAppearSide('right')
                            setCurNode(child)
                          }}
                        >
                          <span>{child.title}</span>
                          <span>{'>'}</span>
                        </button>
                      )
                    )}
                {curNode.parent !== null && !curNode.expandir && (
                  <button
                    className={classNames(
                      'mt-4 px-4 py-2 text-left',
                      buttonClass
                    )}
                    onClick={handleBack}
                  >
                    {'< Voltar'}
                  </button>
                )}
              </>
            )}
          </div>
        </Scrollbar>
      </div>
    </div>
  )
}

export default SideBar
